import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, Building2, Lock, Megaphone } from "lucide-react";
import { lookbookApi } from "@/api/lookbook-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { MultiSelect } from "@/components/ui/multi-select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RaiseBoardNav, RaiseBoardShell, RBCard } from "./rb-ui";
import { BoardRow, HeroStat } from "./board-row";
import {
  getApplicantCompanyId,
  getFilterValues,
  matchesFilter,
  uniqueSorted,
  type Application,
} from "./deal-fields";
import { useMyVotes, useToggleUpvote } from "./use-raise-board-actions";
import { SubscribeButton } from "./subscribe-button";
import { InviteButton } from "./invite-button";

/**
 * A lookbook — a private raiseboard, reachable only by its link.
 *
 * Deliberately the same reading experience as the global board: same masthead,
 * same filters, same cards. What differs is that the deals are one workspace's
 * hand-picked selection, and the upvotes and comments shown are the lookbook's
 * own rather than the public tallies (the server swaps those in, so the shared
 * card needs no say in it).
 */
export default function LookbookPublic() {
  const { lookbookPublicId } = useParams<{ lookbookPublicId: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [businessModelFilter, setBusinessModelFilter] = useState<string[]>([]);
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["lookbook", lookbookPublicId],
    queryFn: () => lookbookApi.getByPublicId(lookbookPublicId as string),
    enabled: !!lookbookPublicId,
    retry: false,
    refetchOnWindowFocus: true,
  });

  // A signed-out visitor votes as a guest when the owner allows it; signing in
  // always wins, so a member's vote is never filed under their browser id.
  const guest = !isSignedIn && data?.result?.lookbook?.guest_engagement === true;
  const votedIds = useMyVotes(!!isSignedIn || guest, lookbookPublicId, guest);
  const toggleVote = useToggleUpvote({ lookbookPublicId, guest });

  const handleVote = (application: Application) => {
    if (!isSignedIn && !guest) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    toggleVote.mutate(String(application._id));
  };

  const openDeal = (application: Application) => {
    const applicantId = getApplicantCompanyId(application);
    if (!applicantId) {
      toast.error("This deal's profile isn't available.");
      return;
    }
    // Stays inside the lookbook rather than routing through /rb/:slug — the
    // deal's own board may not even be public, and the visitor's access came
    // from this link.
    navigate(`/lb/${lookbookPublicId}/${applicantId}`);
  };

  const applications = useMemo(
    () => data?.result?.applications ?? [],
    [data]
  );
  const lookbookName = data?.result?.lookbook?.name ?? "Lookbook";
  const ownerName = data?.result?.opportunity?.opportunity_name;

  const filterOptions = useMemo(() => {
    const industries: string[] = [];
    const businessModels: string[] = [];
    for (const app of applications) {
      const v = getFilterValues(app);
      industries.push(...v.industries);
      businessModels.push(...v.businessModels);
    }
    return {
      industries: uniqueSorted(industries),
      businessModels: uniqueSorted(businessModels),
    };
  }, [applications]);

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      const v = getFilterValues(app);
      return (
        matchesFilter(v.industries, industryFilter) &&
        matchesFilter(v.businessModels, businessModelFilter)
      );
    });
  }, [applications, industryFilter, businessModelFilter]);

  const hasFilters =
    industryFilter.length > 0 || businessModelFilter.length > 0;

  const totalUpvotes = useMemo(
    () => applications.reduce((sum, a) => sum + (a.upvote_count ?? 0), 0),
    [applications]
  );

  const toOptions = (values: string[]) =>
    values.map((v) => ({ label: v, value: v }));

  if (error) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-32 text-center">
          <Lock className="h-10 w-10 text-muted-foreground/40" />
          <p className="font-serif text-2xl">This lookbook isn't available</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            The link may have expired, or its owner may have taken it offline.
          </p>
        </div>
        <BoardAuthDialog
          open={!!authReason}
          onOpenChange={(open) => {
            if (!open) setAuthReason(null);
          }}
          reason={authReason ?? undefined}
        />
      </RaiseBoardShell>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />

        {/* Same masthead treatment as the global board, but naming the
            lookbook and whose selection it is. */}
        <div className="border-b border-border/40 bg-foreground text-background">
          <div className="mx-auto flex max-w-4xl flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:py-12">
            <div className="max-w-xl">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-background/50">
                <Lock className="h-3 w-3" />
                Private Lookbook
              </p>
              <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
                {lookbookName}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background/70">
                {ownerName
                  ? `A hand-picked selection from ${ownerName}, shared with you privately.`
                  : "A hand-picked selection, shared with you privately."}
              </p>
            </div>

            <div className="flex flex-col items-start gap-5 lg:items-end lg:border-l lg:border-background/15 lg:pl-10">
              <div className="flex items-start gap-8">
                <HeroStat value={applications.length} label="Startups" />
                <HeroStat value={totalUpvotes} label="Endorsements" />
              </div>
              {/* On the inverted hero band, so it takes the light treatment
                  the global board's CTA uses rather than the default ink fill.
                  The border is set too: the subscribed state switches to the
                  outline variant, whose default border-input disappears
                  against this background. */}
              <div className="flex flex-wrap items-center gap-2">
                <SubscribeButton
                  lookbookPublicId={lookbookPublicId}
                  onRequestAuth={setAuthReason}
                  className="border-background/40 bg-background text-foreground hover:bg-background/90 hover:text-foreground"
                />
                {/* Owners only — renders nothing for anyone else. Outline on
                    the inverted band, so it reads as secondary to Subscribe. */}
                <InviteButton
                  opportunityId={data?.result?.opportunity?._id}
                  lookbookId={data?.result?.lookbook?._id}
                  passwordProtected={data?.result?.lookbook?.password_protected}
                  className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Single column: every deal here comes from the one workspace, so the
            global board's "which board is this from" rail has nothing to say. */}
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
          <h1 className="font-serif text-5xl leading-none sm:text-6xl">
            {lookbookName}
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Startups actively raising — ranked by momentum inside this lookbook
          </p>
          <p className="mt-3 border-l-2 border-border pl-3 text-sm text-muted-foreground">
            {isLoading ? (
              "Loading the lookbook…"
            ) : (
              <>
                {filtered.length} compan{filtered.length === 1 ? "y" : "ies"}
                {hasFilters ? ` of ${applications.length}` : ""}
              </>
            )}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <MultiSelect
              id="lookbook-industry"
              options={toOptions(filterOptions.industries)}
              value={industryFilter}
              onValueChange={setIndustryFilter}
              placeholder="Industry"
              triggerIcon={Building2}
              maxCount={2}
              className="w-auto min-w-[150px] rounded-full border-border bg-card"
            />
            <MultiSelect
              id="lookbook-business-model"
              options={toOptions(filterOptions.businessModels)}
              value={businessModelFilter}
              onValueChange={setBusinessModelFilter}
              placeholder="Business model"
              triggerIcon={Briefcase}
              maxCount={2}
              className="w-auto min-w-[150px] rounded-full border-border bg-card"
            />
          </div>

          <div className="mt-6 space-y-3">
            {isLoading ? (
              [0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[104px] w-full rounded-2xl" />
              ))
            ) : filtered.length === 0 ? (
              <RBCard className="py-12 text-center">
                <Megaphone className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">
                  {applications.length === 0
                    ? "No companies have been added to this lookbook yet."
                    : "No companies match the current filters."}
                </p>
              </RBCard>
            ) : (
              filtered.map((application, index) => (
                <BoardRow
                  key={String(application._id)}
                  application={application}
                  rank={index + 1}
                  vote={{
                    voted: votedIds.has(String(application._id)),
                    count: application.upvote_count ?? 0,
                  }}
                  onVote={() => handleVote(application)}
                  onOpen={() => openDeal(application)}
                  showSource={false}
                />
              ))
            )}
          </div>
        </main>

        <BoardAuthDialog
          open={!!authReason}
          onOpenChange={(open) => {
            if (!open) setAuthReason(null);
          }}
          reason={authReason ?? undefined}
        />
      </RaiseBoardShell>
    </TooltipProvider>
  );
}
